import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  CatalogListResponse,
  CreateLiveResponse,
  LiveCapabilitiesResponse,
  LiveDetailResponse,
  LiveDraftContextResponse,
  LiveDraftProductItem,
  LiveListResponse,
} from "./types";

const base = `${config.apiUrl}/live`;

export const liveApi = {
  list(params?: {
    pageId?: string;
    q?: string;
    status?: "draft" | "live" | "ended" | "other";
    limit?: number;
    offset?: number;
  }): Promise<LiveListResponse> {
    const q = new URLSearchParams();
    if (params?.pageId) q.set("pageId", params.pageId);
    if (params?.q) q.set("q", params.q);
    if (params?.status) q.set("status", params.status);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get(`${base}${qs ? `?${qs}` : ""}`);
  },

  createDraft(body: {
    pageId: string;
    title?: string;
    warehouseId?: string;
  }): Promise<{ draft: { id: string } }> {
    return fetcher.post(`${base}/drafts`, body);
  },

  capabilities(pageId: string): Promise<LiveCapabilitiesResponse> {
    return fetcher.get(
      `${base}/capabilities?pageId=${encodeURIComponent(pageId)}`,
      { silentError: true },
    );
  },

  getDraft(id: string): Promise<LiveDraftContextResponse> {
    return fetcher.get(`${base}/drafts/${id}`);
  },

  patchDraft(
    id: string,
    body: {
      title?: string;
      description?: string | null;
      warehouseId?: string | null;
    },
  ): Promise<{ draft: { id: string } }> {
    return fetcher.patch(`${base}/drafts/${id}`, body);
  },

  discardDraft(id: string): Promise<{ ok: true }> {
    return fetcher.delete(`${base}/drafts/${id}`);
  },

  draftCatalog(
    draftId: string,
    params?: {
      q?: string;
      limit?: number;
      offset?: number;
      groupByProduct?: boolean;
    },
  ): Promise<CatalogListResponse> {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    if (params?.groupByProduct) q.set("groupByProduct", "true");
    const qs = q.toString();
    return fetcher.get(
      `${base}/drafts/${draftId}/catalog${qs ? `?${qs}` : ""}`,
    );
  },

  addDraftProduct(
    draftId: string,
    body: {
      variantId: string;
      cfCode?: string;
      price?: number;
      allocatedQuantity?: number;
      allocationMode?: "limit" | "unlimited";
    },
  ): Promise<{ product: LiveDraftProductItem }> {
    return fetcher.post(`${base}/drafts/${draftId}/products`, body);
  },

  addDraftProducts(
    draftId: string,
    items: Array<{
      variantId: string;
      cfCode?: string;
      price?: number;
      allocatedQuantity?: number;
      allocationMode?: "limit" | "unlimited";
    }>,
  ): Promise<{
    added: string[];
    failed: Array<{ variantId: string; code: string }>;
  }> {
    return fetcher.post(`${base}/drafts/${draftId}/products`, { items });
  },

  addDraftProductsBySelection(
    draftId: string,
    selection:
      | { mode: "selected"; variantIds: string[] }
      | {
          mode: "all_matching";
          q?: string;
          excludedVariantIds?: string[];
        },
    allocation: {
      allocationMode?: "limit" | "unlimited";
      allocatedQuantity?: number;
    } = {},
  ): Promise<{
    added: string[];
    skipped: string[];
    failed: Array<{ variantId: string; code: string }>;
  }> {
    return fetcher.post(`${base}/drafts/${draftId}/products`, {
      selection,
      ...allocation,
    });
  },

  patchDraftProduct(
    draftId: string,
    productId: string,
    body: {
      cfCode?: string;
      price?: number;
      allocatedQuantity?: number;
      saleEnabled?: boolean;
      allocationMode?: "limit" | "unlimited";
    },
  ): Promise<{ product: LiveDraftProductItem }> {
    return fetcher.patch(
      `${base}/drafts/${draftId}/products/${productId}`,
      body,
    );
  },

  removeDraftProduct(
    draftId: string,
    productId: string,
  ): Promise<{ ok: true }> {
    return fetcher.delete(`${base}/drafts/${draftId}/products/${productId}`);
  },

  createLive(
    draftId: string,
    idempotencyKey: string,
  ): Promise<CreateLiveResponse> {
    return fetcher.postWithHeaders(
      `${base}/drafts/${draftId}/create`,
      {},
      { "Idempotency-Key": idempotencyKey },
      { silentError: true },
    );
  },

  connectExistingLive(
    draftId: string,
    liveReference: string,
  ): Promise<CreateLiveResponse> {
    return fetcher.post(
      `${base}/drafts/${draftId}/connect-existing`,
      { liveReference },
      { silentError: true },
    );
  },

  getDetail(feedId: string): Promise<LiveDetailResponse> {
    return fetcher.get(`${base}/${feedId}`);
  },

  syncStatus(feedId: string): Promise<{
    feedId: string;
    graphStatus: string | null;
    normalizedStatus: string;
    permalinkUrl: string | null;
    liveViews: number | null;
  }> {
    return fetcher.post(`${base}/${feedId}/sync-status`);
  },

  endLive(feedId: string): Promise<{ ok: true; feedId: string }> {
    return fetcher.post(`${base}/${feedId}/end`);
  },
};
